import React, { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { LiProps } from '../../../../types/HtmlProps';
import { Namespace } from '../../../../translation/namespace';
import { CarouselCounter } from './CarouselCounter';
import { CarouselLink } from './CarouselLink';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';

type CarouselListItemProps = LiProps & {
  total: number;
  currentIndex: number;
  id: string;
  tooltipPlacement?: 'inside' | 'outside';
  ref?: React.RefObject<HTMLLIElement>;
};

export function CarouselListItem({
  children,
  total,
  currentIndex,
  id,
  tooltipPlacement = 'inside',
  ref,
  ...listItemProps
}: PropsWithChildren<CarouselListItemProps>) {
  const { t } = useTranslation(Namespace.ImageGallery);
  const getSlideId = (number: number) => `carousel-${id}-slide-${number}`;
  const first = 1;
  const last = total;
  const prev = currentIndex;
  const current = currentIndex + 1;
  const next = currentIndex + 2;
  const prevLinkProps =
    currentIndex === 0
      ? {
          href: `#${getSlideId(last)}`,
          title: t('buttonLast', { number: last }),
        }
      : {
          href: `#${getSlideId(prev)}`,
          title: t('buttonPrev', { number: prev }),
        };

  const nextLinkProps =
    next > total
      ? {
          href: `#${getSlideId(first)}`,
          title: t('buttonFirst', { number: first }),
        }
      : {
          href: `#${getSlideId(next)}`,
          title: t('buttonNext', { number: next }),
        };

  return (
    <li
      id={getSlideId(current)}
      className="image-gallery__carousel-slide"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      ref={ref}
      {...listItemProps}
    >
      <div className="image-gallery__carousel-snapper">
        <CarouselCounter current={current} total={total} />
        {children}
        {total > 1 ? (
          <div className="image-gallery__carousel-stuff-container">
            <CarouselLink
              className="image-gallery__carousel-prev"
              key={`id-${currentIndex}-prev`}
              tooltipPlacement={tooltipPlacement === 'outside' ? TooltipPlacement.RIGHT : TooltipPlacement.LEFT}
              {...prevLinkProps}
            />
            <CarouselLink
              key={`id-${currentIndex}-next`}
              className="image-gallery__carousel-next"
              tooltipPlacement={tooltipPlacement === 'outside' ? TooltipPlacement.LEFT : TooltipPlacement.RIGHT}
              {...nextLinkProps}
            />
          </div>
        ) : null}
      </div>
    </li>
  );
}
